import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { blogEndpoints } from "../../services/api";
import { motion } from "framer-motion";
import { Trash2 } from "lucide-react";

const { GET_ALL_BLOG, DELETE_BLOG_BY_ID } = blogEndpoints;

export default function AllBlogs({ enableDelete = false }) {
  const [blogs, setBlogs] = useState([]);
  const [visibleBlogs, setVisibleBlogs] = useState([]);
  const [blogsToShow, setBlogsToShow] = useState(6);
  const navigate = useNavigate();

  useEffect(() => {
    toast.loading("Loading ...");

    fetch(GET_ALL_BLOG)
      .then((res) => res.json())
      .then((data) => {
        setBlogs(data);
        setVisibleBlogs(data.slice(0, blogsToShow));
        toast.dismiss();
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Failed to load blogs. Please try again.");
      });
  }, []);

  const loadMoreBlogs = () => {
    const newLimit = blogsToShow + 6;
    setBlogsToShow(newLimit);
    setVisibleBlogs(blogs.slice(0, newLimit));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;

    const toastId = toast.loading("Deleting blog...");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${DELETE_BLOG_BY_ID(id)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        credentials: "include",
      });

      const data = await res.json();
      if (res.ok) {
        const updatedBlogs = blogs.filter((blog) => blog._id !== id);
        setBlogs(updatedBlogs);
        setVisibleBlogs(updatedBlogs.slice(0, blogsToShow));
        toast.dismiss(toastId);
        toast.success("Blog deleted successfully");
      } else {
        throw new Error(data.message || "Failed to delete blog");
      }
    } catch {
      toast.dismiss(toastId);
      toast.error("Failed to delete blog");
    }
  };

  return (
    <div className="w-full flex flex-col items-center justify-center py-16 px-6 bg-[#0F172A] text-white">
      {/* Section Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
        <h2 className="text-4xl font-bold">
          <span className="text-[#C9A227]">Legal Insights & Updates</span>
        </h2>
        <p className="text-gray-400 text-lg mt-2 max-w-3xl mx-auto">
          Stay informed with our latest articles on legal matters, industry trends, and expert advice.
        </p>
      </motion.div>

      {/* Blogs Grid */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-10 max-w-7xl">
        {visibleBlogs.map((blog) => (
          <motion.div
            key={blog._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            className="relative bg-white p-6 rounded-lg shadow-lg text-black group overflow-hidden transition-all duration-300 cursor-pointer"
            onClick={() => navigate(`/blog/${blog._id}`)}
          >
            {/* Thumbnail with Hover Effect */}
            <div className="relative overflow-hidden rounded-lg">
              <img
                src={blog.thumbnail}
                alt={blog.title}
                className="w-full h-48 object-cover rounded-md transform transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Blog Title */}
            <h3 className="text-2xl font-semibold text-[#0F172A] mt-4 transition-all duration-300 group-hover:text-[#C9A227]">
              {blog.title}
            </h3>

            {/* Blog Description */}
            <p className="text-gray-600 mt-2 transition-opacity duration-300 group-hover:text-gray-800">
              {blog.description.substring(0, 100)}...
            </p>

            {/* Read More with Underline Animation */}
            <p className="mt-4 text-[#C9A227] font-bold relative inline-block">
              Read More →
              <span className="absolute left-0 bottom-0 w-0 h-1 bg-[#C9A227] transition-all duration-300 group-hover:w-full"></span>
            </p>

            {/* Delete Button (Admin Only) */}
            {enableDelete && (
              <motion.div whileHover={{ scale: 1.2, rotate: 10 }} transition={{ duration: 0.2 }} className="absolute top-4 right-4">
                <button onClick={(e) => { e.stopPropagation(); handleDelete(blog._id); }} className="bg-red-600 p-2 rounded-full text-white hover:bg-red-700 transition-all duration-300">
                  <Trash2 size={20} />
                </button>
              </motion.div>
            )}
          </motion.div>
        ))}
      </motion.div>

      {/* Load More Button */}
      {visibleBlogs.length < blogs.length && (
        <motion.button
          onClick={loadMoreBlogs}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="mt-10 px-6 py-3 border border-[#C9A227] text-[#C9A227] font-semibold rounded-lg hover:bg-[#C9A227] hover:text-[#0F172A] transition"
        >
          Load More
        </motion.button>
      )}
    </div>
  );
}
